import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chatbot, ChatbotTemplate } from '../types/chatbot';
import { AI_AGENTS_DATA } from '../data/agents';
import { agentInstanceService } from '../services/agentService';
import { chatbotService } from '../services/chatbotService';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { toast } from 'react-toastify';
import { convertMillisecondsToSeconds } from '../utility/time';

import { LogoUploader, S3Params } from '../components/LogoUploader';
import { ShowImage } from '../utils/ShowImage';


export const Chatbots: React.FC = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [greetingMessage, setGreetingMessage] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');

  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);


  const [dynamicChatbots, setDynamicChatbots] = useState<any>([]);
  const [chatbotsLoading, setChatbotsLoading] = useState(false);
  const [chatbotsError, setChatbotsError] = useState<string | null>(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editGreeting, setEditGreeting] = useState('');
  const [editPlaceholder, setEditPlaceholder] = useState('');
  const [editSelectedAgentIds, setEditSelectedAgentIds] = useState<string[]>([]);
  const [editingSaving, setEditingSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

    // --- logo states (cropped data URLs) ---
    const [createLogo, setCreateLogo] = useState<S3Params | null>(null);
    const [editLogo, setEditLogo] = useState<S3Params | null>(null);



  const displayedChatbots : any = dynamicChatbots && dynamicChatbots.length > 0 ? dynamicChatbots : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'training': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'maintenance': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '🟢';
      case 'inactive': return '⚪';
      case 'training': return '🟡';
      case 'maintenance': return '🔵';
      default: return '⚪';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'customer-support': return '💬';
      case 'sales': return '💰';
      case 'technical': return '🔧';
      case 'general': return '🤖';
      case 'specialized': return '⚡';
      default: return '🤖';
    }
  };

  // ---- load agent instances for picker (dynamic-only) ----
  useEffect(() => {
    let mounted = true;
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        const resp = await agentInstanceService.listAgentInstance({ });
        const items = (resp as any).agentInstances ?? resp ?? [];
        const mapped = (items || []).map((it: any) => ({
          id: it.id,
          name: it.name ?? it.code ?? `Agent ${it.id}`,
          lastActive: it.updatedAt ? new Date(it.updatedAt).toLocaleString() : undefined,
          type: it.agentType?.name ?? it.agentType?.code ?? 'unknown',
        }));
        if (mounted) setAgentInstances(mapped);
      } catch (err) {
        console.error('Failed to load agent instances', err);
        if (mounted) setAgentInstances([]); 
      } finally {
        if (mounted) setAgentsLoading(false);
      }
    };
    loadAgents();
    return () => { mounted = false; };
  }, []);


  const loadChatbots = async () => {
    setChatbotsLoading(true);
    setChatbotsError(null);
    try {
      const res = await chatbotService.listChatbots({search:searchTerm});
      const items = (res as any).chatbots ?? res ?? [];
      const mapped = (items || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        description: c.description,
        imgLink:c.imgLink,
        s3ObjectData:c.s3ObjectData,
        type: 'general',
        status: c.state === 'active' ? 'active' : (c.state ?? 'inactive'),
        model: c.model ?? 'N/A',
        version: `v${c.version ?? c.schemaVersion ?? 1}`,
        language: c.language ?? 'English',
        createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
        updatedAt: c.updatedAt ? new Date(c.updatedAt) : new Date(),
        conversations: c._count?.conversations?? 0,
        satisfaction: c.metrics?.satisfaction ?? 0,
        responseTime: c.metrics?.responseTime ?? '—',
        tags: c.tags ?? [],
        configuration: c.config ?? {},
        avgResponseTime: c?.avgResponseTime ?? 0,
        __raw: c
      }));
      setDynamicChatbots(mapped);
    } catch (err: any) {
      console.error('Failed to load chatbots', err);
       {
        setChatbotsError('Failed to load chatbots');
        setDynamicChatbots([]); 
      }
    } finally {
      setChatbotsLoading(false);
    }
  };
  useEffect(() => {
    loadChatbots();
  }, []);

    useEffect(() => {
      let t = setTimeout(() => {
        loadChatbots();
      }, 300);
      return () => clearTimeout(t);
    }, [searchTerm]);

  const pickerAgents = useMemo(() => agentInstances || [], [agentInstances]);

  // ---------- Edit flow helpers ----------
  const openEdit = (chatbot: any) => {
    const raw = (chatbot as any).__raw ?? chatbot;
    setEditingId(chatbot.id);
    setEditName(raw.name ?? chatbot.name ?? '');
    setEditDescription(raw.description ?? chatbot.description ?? '');
    setEditGreeting(raw.config?.greeting ?? raw.configuration?.welcomeMessage ?? '');
    setEditPlaceholder(raw.config?.placeholder ?? '');
    const agentIds = (raw.agents ?? []).map((a: any) => a.agentInstanceId).filter(Boolean);
    setEditSelectedAgentIds(agentIds);
    setIsEditOpen(true);
    setEditLogo(raw.s3ObjectData ?? " ");
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditGreeting('');
    setEditPlaceholder('');
    setEditSelectedAgentIds([]);
    setEditLogo(null);
  };

  const doUpdate = async () => {
    if (!editingId) return;
    setEditingSaving(true);


    try {
      const payload: any = {
        name: editName.trim(),
        description: editDescription.trim(),
        config: {
          greeting: editGreeting || undefined,
          placeholder: editPlaceholder || undefined,
            // logo: editLogo,
        },
        s3ObjectData:editLogo,
        agentInstanceIds: editSelectedAgentIds,
      };
      const updated = await chatbotService.updateChatbot(editingId, payload);
      toast.success(updated?.message||"Chatbot updated successfully!")
      loadChatbots()
      
      closeEdit();
    } catch (err) {
      console.error('Failed to update chatbot', err);
    } finally {
      setEditingSaving(false);
    }
  };

  // ---------- Delete flow ----------
  const askDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res:any = await chatbotService.deleteChatbot(deleteId);
      toast.success(res?.message||"Chatbot deleted successfully!")
      loadChatbots()

    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // ---------- Create flow (unchanged, but posts to API) ----------
  const doCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!createName.trim() || !createDescription.trim() || selectedAgentIds.length === 0) return;
    const code = createName.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '').slice(0, 100);
    const payload: any = {
      name: createName.trim(),
      code,
      description: createDescription.trim(),
      isActive: true,
      state: 'draft',
      config: {
        greeting: greetingMessage || undefined,
        placeholder: placeholderText || undefined,
        // logo: createLogo,
      },
      s3ObjectData:createLogo,
      schemaVersion: 1,
      agentInstanceIds: selectedAgentIds,
    };
    try {
      const created = await chatbotService.createChatbot(payload);
      setIsCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      setGreetingMessage('');
      setPlaceholderText('');
      setSelectedAgentIds([]);
      setAgentPickerOpen(false);
      setAgentSearch('');
      setCreateLogo(null);
      toast.success(created?.message||"Chatbot created successfully!")
      loadChatbots()
      
    } catch (err) {
      console.error('Create chatbot failed', err);
    }
  };

  // ---------- filteredChatbots (unchanged) ----------
  const filteredChatbots = displayedChatbots.filter((chatbot:any) => {
    const matchesSearch = chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chatbot.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (chatbot.tags ?? []).some((tag:any) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || chatbot.status === statusFilter;
    const matchesType = typeFilter === 'all' || chatbot.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // ---------- UI (kept identical except added Edit/Delete actions & modals) ----------
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
        {/* Header Section */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">
                  AI Chatbots
                </h1>
                <p className="mt-2 text-agentic-text-secondary">
                  Create, manage, and monitor your AI-powered chatbots
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="mt-4 lg:mt-0 flex space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-agentic-text-primary">{displayedChatbots.length}</div>
                  <div className="text-sm text-agentic-text-secondary">Total Bots</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-agentic-accent-green">{displayedChatbots.filter((c:any) => c.status === 'draft').length}</div>
                  <div className="text-sm text-agentic-text-secondary">Draft</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-agentic-accent-yellow">{displayedChatbots.filter((c:any) => c.status === 'training').length}</div>
                  <div className="text-sm text-agentic-text-secondary">Training</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-agentic-text-muted">🔍</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search chatbots by name, description, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent bg-white/90 backdrop-blur-sm"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                {/* <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent bg-white/90 backdrop-blur-sm"
                >
                  <option value="all">All State</option>
                  <option value="active">Active</option>
                  <option value="inactive">Draft</option>
                </select> */}

                {/* <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent bg-white/90 backdrop-blur-sm"
                >
                  <option value="all">All Types</option>
                  <option value="customer-support">Customer Support</option>
                  <option value="sales">Sales</option>
                  <option value="technical">Technical</option>
                  <option value="general">General</option>
                  <option value="specialized">Specialized</option>
                </select> */}

                {/* View Mode Toggle */}
                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      viewMode === 'grid'
                        ? 'bg-white text-agentic-primary-blue shadow-sm'
                        : 'text-agentic-text-secondary hover:text-agentic-text-primary'
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      viewMode === 'list'
                        ? 'bg-white text-agentic-primary-blue shadow-sm'
                        : 'text-agentic-text-secondary hover:text-agentic-text-primary'
                    }`}
                  >
                    List
                  </button>
                </div>

                {/* Create New Chatbot Button */}
                <button
                  onClick={() => setIsCreateOpen(true)}
                  className="px-6 py-3 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                >
                  + Create Bot
                </button>
              </div>
            </div>
          </div>

          {/* Chatbots Grid/List */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredChatbots.map((chatbot:any) => (
                <div key={chatbot.id} className="group relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-agentic-primary-blue/20 to-agentic-accent-purple/20 rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
                  <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
                    {/* Chatbot Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                      <div className="flex items-center gap-2">
                        {(chatbot?.s3ObjectData || chatbot?.imgLink) ? (
                          <ShowImage
                            s3Params={chatbot?.s3ObjectData}
                            src={chatbot?.imgLink}
                            alt={chatbot.name}
                            className="h-12 w-12 rounded-2xl object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light rounded-2xl flex items-center justify-center shadow-lg">
                            <span className="text-white text-xl">{getTypeIcon(chatbot.type)}</span>
                          </div>
                        )}
                      </div>
                  
                        <div>
                          <h3 className="text-lg font-bold text-agentic-text-primary">{chatbot.name}</h3>
                          {/* <p className="text-sm text-agentic-text-secondary">{(chatbot.type || 'general').replace('-', ' ')} • {chatbot.model}</p> */}
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(chatbot.status)}`}>
                        <span className="mr-1">{getStatusIcon(chatbot.status)}</span>
                        {chatbot.status.charAt(0).toUpperCase() + chatbot.status.slice(1)}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-agentic-text-secondary mb-4 line-clamp-2">{chatbot.description}</p>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-gradient-to-br from-agentic-gray-50 to-agentic-gray-100 rounded-xl">
                        <div className="text-lg font-bold text-agentic-text-primary">{(chatbot.conversations ?? 0).toLocaleString()}</div>
                        <div className="text-xs text-agentic-text-secondary">Conversations</div>
                      </div>
                      <div className="text-center p-3 bg-gradient-to-br from-agentic-gray-50 to-agentic-gray-100 rounded-xl">
                        <div className="text-lg font-bold text-agentic-accent-green">{chatbot?.avgResponseTime ? `${convertMillisecondsToSeconds(chatbot.avgResponseTime)} (sec)`:"-"}</div>
                        <div className="text-xs text-agentic-text-secondary">Response Time</div>
                      </div>
                    </div>

                    {/* Performance Indicators */}
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        {/* <span className="text-xs text-agentic-text-secondary">Language</span>
                        <span className="text-xs font-semibold text-agentic-text-primary">{chatbot.language}</span> */}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-agentic-text-secondary">Version</span>
                        <span className="text-xs font-semibold text-agentic-text-primary">{chatbot.version.replace('v','')}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(chatbot.tags ?? []).map((tag:any, index:any) => (
                        <span key={index} className="px-2 py-1 bg-agentic-primary-blue/10 text-agentic-primary-blue text-xs rounded-lg">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Actions (added Edit & Delete but kept existing Configure & Test) */}
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => navigate(`/chatbots/${chatbot.id}/config`)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                      >
                        Configure
                      </button>

                      <button
                        onClick={() => openEdit(chatbot)}
                        className="px-4 py-2 bg-gradient-to-r from-agentic-accent-yellow to-agentic-accent-yellow text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                        title="Edit"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => askDelete(chatbot.id)}
                        className="px-4 py-2 bg-gradient-to-r from-agentic-accent-red to-agentic-accent-red text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                        title="Delete"
                      >
                        Delete
                      </button>

                      {/* <button className="px-4 py-2 bg-gradient-to-r from-agentic-accent-green to-agentic-accent-green text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200">
                        Test
                      </button> */}
                    </div>

                    {/* Last Updated */}
                    <div className="mt-3 text-xs text-agentic-text-muted text-center">
                      Updated: {chatbot.updatedAt instanceof Date ? chatbot.updatedAt.toLocaleDateString() : new Date(chatbot.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-agentic-gray-50 to-agentic-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Chatbot</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Conversations</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Response Time</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Last Updated</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredChatbots.map((chatbot:any) => (
                      <tr key={chatbot.id} className="hover:bg-agentic-gray-50/50 transition-colors duration-200">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            {/* <div className="w-10 h-10 bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light rounded-xl flex items-center justify-center shadow-sm">
                              <span className="text-white text-lg">{getTypeIcon(chatbot.type)}</span>
                            </div> */}
                        <div className="flex items-center gap-2">
                        {(chatbot?.s3ObjectData || chatbot?.imgLink) ? (
                          <ShowImage
                            s3Params={chatbot?.s3ObjectData}
                            src={chatbot?.imgLink}
                            alt={chatbot.name}
                            className="h-12 w-12 rounded-2xl object-cover"
                            // onError={(e) => {
                            //   e.currentTarget.src = '/default-bot.webp'; 
                            // }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light rounded-2xl flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl">{getTypeIcon(chatbot.type)}</span>
                        </div>
                        )}

                      </div>
                            <div>
                              <div className="text-sm font-semibold text-agentic-text-primary">{chatbot.name}</div>
                          
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(chatbot.status)}`}>
                            <span className="mr-1">{getStatusIcon(chatbot.status)}</span>
                            {chatbot.status.charAt(0).toUpperCase() + chatbot.status.slice(1)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-semibold text-agentic-text-primary">{(chatbot.conversations ?? 0).toLocaleString()}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-agentic-text-secondary">
                          {chatbot?.avgResponseTime ? `${convertMillisecondsToSeconds(chatbot.avgResponseTime)} (sec)`:"-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-agentic-text-secondary">
                          {chatbot.updatedAt instanceof Date ? chatbot.updatedAt.toLocaleDateString() : new Date(chatbot.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => navigate(`/chatbots/${chatbot.id}/config`)}
                              className="px-3 py-1 bg-agentic-primary-blue text-white text-xs font-medium rounded-lg hover:bg-agentic-primary-blue-dark transition-colors duration-200"
                            >
                              Configure
                            </button>

                            <button
                              onClick={() => openEdit(chatbot)}
                              className="px-3 py-1 bg-agentic-accent-yellow text-white text-xs font-medium rounded-lg hover:opacity-90 transition-colors duration-200"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => askDelete(chatbot.id)}
                              className="px-3 py-1 bg-agentic-accent-red text-white text-xs font-medium rounded-lg hover:opacity-90 transition-colors duration-200"
                            >
                              Delete
                            </button>

                            {/* <button className="px-3 py-1 bg-agentic-accent-green text-white text-xs font-medium rounded-lg hover:opacity-90 transition-colors duration-200">
                              Test
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {filteredChatbots.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🤖</div>
              <h3 className="text-xl font-semibold text-agentic-text-primary mb-2">No chatbots found</h3>
              <p className="text-agentic-text-secondary mb-6">Create your first AI chatbot to get started</p>
              <button
                onClick={() => setIsCreateOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200"
              >
                Create Your First Bot
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Chatbot Modal (unchanged behaviour but integrated with API) */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsCreateOpen(false)} />
          <div className="relative w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">Create Chatbot</h2>
              <p className="text-sm text-agentic-text-secondary mt-1">Define a new chatbot and assign agents.</p>
            </div>
            <form onSubmit={doCreate} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Chatbot Name</label>
                  <input
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="e.g., Pacific One Health"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Description</label>
                  <textarea
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    placeholder="Describe this chatbot"
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <LogoUploader
                    label="Chatbot Logo"
                    value={createLogo}
                    onChange={setCreateLogo}
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Assign Agents</label>
                  <div
                    className="w-full rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-agentic-primary-blue focus-within:border-transparent px-2 py-2 flex flex-wrap gap-2 relative cursor-text"
                    tabIndex={0}
                    onClick={() => setAgentPickerOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAgentPickerOpen((v) => !v); } }}
                    aria-expanded={agentPickerOpen}
                    role="combobox"
                    aria-haspopup="listbox"
                  >
                    {selectedAgentIds.length === 0 && (
                      <span className="text-sm text-agentic-text-muted px-1 py-1">Select agents…</span>
                    )}
                    {selectedAgentIds.map((id) => {
                      const agent = pickerAgents.find((a) => a.id === id);
                      if (!agent) return null;
                      return (
                        <span key={id} className="px-2 py-1 rounded-full bg-agentic-primary-blue/10 text-agentic-primary-blue text-xs inline-flex items-center gap-2">
                          {agent.name} • Last updated: {agent.lastActive}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedAgentIds(selectedAgentIds.filter((x) => x !== id)); }}
                            className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
                            aria-label="Remove agent"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    <div className="flex-1 min-w-[200px]" />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}
                      className="absolute right-2 top-2 px-2 py-1 text-agentic-text-secondary hover:text-agentic-text-primary"
                      aria-label="Toggle agents"
                    >
                      ▾
                    </button>

                    {agentPickerOpen && (
                      <div className="absolute left-0 top-full mt-2 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg z-10">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <input
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            placeholder="Search agents"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                          />
                        </div>
                        <div className="p-2">
                          {agentsLoading && <div className="p-2 text-sm text-agentic-text-secondary">Loading agents...</div>}
                          {!agentsLoading && pickerAgents.length === 0 && <div className="p-2 text-sm text-agentic-text-secondary">No agents available</div>}
                          {!agentsLoading && pickerAgents.length > 0 && pickerAgents
                            .filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
                            .map((agent) => {
                              const checked = selectedAgentIds.includes(agent.id);
                              return (
                                <button
                                  type="button"
                                  key={agent.id}
                                  onClick={() => {
                                    if (checked) setSelectedAgentIds(selectedAgentIds.filter((x) => x !== agent.id));
                                    else setSelectedAgentIds([...selectedAgentIds, agent.id]);
                                  }}
                                  className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 ${checked ? 'bg-agentic-primary-blue/5' : ''}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <input type="checkbox" readOnly checked={checked} className="pointer-events-none" />
                                    <div>
                                      <div className="text-sm font-medium text-agentic-text-primary">{agent.name}</div>
                                      <div className="text-xs text-agentic-text-secondary">Last updated: {agent.lastActive}</div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-agentic-text-secondary">{agent.type}</span>
                                </button>
                              );
                            })}
                        </div>
                        <div className="sticky bottom-0 bg-white p-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-agentic-text-secondary">{selectedAgentIds.length} selected</span>
                          <div className="flex items-center gap-2">
                            <button type="button" className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50" onClick={() => setSelectedAgentIds([])}>Clear</button>
                            <button type="button" className="px-3 py-1 text-sm rounded-lg text-white bg-agentic-primary-blue hover:opacity-90"    onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}>Done</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Greeting Message</label>
                  <input
                    value={greetingMessage}
                    onChange={(e) => setGreetingMessage(e.target.value)}
                    placeholder="Hello! How can I help you today?"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Placeholder Text</label>
                  <input
                    value={placeholderText}
                    onChange={(e) => setPlaceholderText(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light hover:shadow-md disabled:opacity-60"
                  disabled={!createName.trim() || !createDescription.trim() || selectedAgentIds.length === 0}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Chatbot Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => closeEdit()} />
          <div className="relative w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">Edit Chatbot</h2>
              <p className="text-sm text-agentic-text-secondary mt-1">Update chatbot details and assigned agents.</p>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Chatbot Name</label>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g., Pacific One Health"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Describe this chatbot"
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <LogoUploader
                    label="Chatbot Logo"
                    value={editLogo}
                    onChange={setEditLogo}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">* Assign Agents</label>
                  <div
                    className="w-full rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-agentic-primary-blue focus-within:border-transparent px-2 py-2 flex flex-wrap gap-2 relative cursor-text"
                    tabIndex={0}
                    onClick={() => setAgentPickerOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAgentPickerOpen((v) => !v); } }}
                    aria-expanded={agentPickerOpen}
                    role="combobox"
                    aria-haspopup="listbox"
                  >
                    {editSelectedAgentIds.length === 0 && (
                      <span className="text-sm text-agentic-text-muted px-1 py-1">Select agents…</span>
                    )}
                    {editSelectedAgentIds.map((id) => {
                      const agent = pickerAgents.find((a) => a.id === id);
                      if (!agent) return null;
                      return (
                        <span key={id} className="px-2 py-1 rounded-full bg-agentic-primary-blue/10 text-agentic-primary-blue text-xs inline-flex items-center gap-2">
                          {agent.name} • Last updated: {agent.lastActive}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditSelectedAgentIds(editSelectedAgentIds.filter((x) => x !== id)); }}
                            className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
                            aria-label="Remove agent"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    <div className="flex-1 min-w-[200px]" />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAgentPickerOpen((v) => !v); }}
                      className="absolute right-2 top-2 px-2 py-1 text-agentic-text-secondary hover:text-agentic-text-primary"
                      aria-label="Toggle agents"
                    >
                      ▾
                    </button>

                    {agentPickerOpen && (
                      <div className="absolute left-0 top-full mt-2 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg z-10">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <input
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            placeholder="Search agents"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                          />
                        </div>
                        <div className="p-2">
                          {agentsLoading && <div className="p-2 text-sm text-agentic-text-secondary">Loading agents...</div>}
                          {!agentsLoading && pickerAgents.length === 0 && <div className="p-2 text-sm text-agentic-text-secondary">No agents available</div>}
                          {!agentsLoading && pickerAgents.length > 0 && pickerAgents
                            .filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
                            .map((agent) => {
                              const checked = editSelectedAgentIds.includes(agent.id);
                              return (
                                <button
                                  type="button"
                                  key={agent.id}
                                  onClick={() => {
                                    if (checked) setEditSelectedAgentIds(editSelectedAgentIds.filter((x) => x !== agent.id));
                                    else setEditSelectedAgentIds([...editSelectedAgentIds, agent.id]);
                                  }}
                                  className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 ${checked ? 'bg-agentic-primary-blue/5' : ''}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <input type="checkbox" readOnly checked={checked} className="pointer-events-none" />
                                    <div>
                                      <div className="text-sm font-medium text-agentic-text-primary">{agent.name}</div>
                                      <div className="text-xs text-agentic-text-secondary">Last updated: {agent.lastActive}</div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-agentic-text-secondary">{agent.type}</span>
                                </button>
                              );
                            })}
                        </div>
                        <div className="sticky bottom-0 bg-white p-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-agentic-text-secondary">{editSelectedAgentIds.length} selected</span>
                          <div className="flex items-center gap-2">
                            <button type="button" className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50" onClick={() => setEditSelectedAgentIds([])}>Clear</button>
                            <button type="button" className="px-3 py-1 text-sm rounded-lg text-white bg-agentic-primary-blue hover:opacity-90" onClick={() => setAgentPickerOpen(false)}>Done</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Greeting Message</label>
                  <input
                    value={editGreeting}
                    onChange={(e) => setEditGreeting(e.target.value)}
                    placeholder="Hello! How can I help you today?"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Placeholder Text</label>
                  <input
                    value={editPlaceholder}
                    onChange={(e) => setEditPlaceholder(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => closeEdit()}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => doUpdate()}
                  className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light hover:shadow-md disabled:opacity-60"
                  disabled={!editName.trim() || !editDescription.trim() || editSelectedAgentIds.length === 0 || editingSaving}
                >
                  {editingSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this chatbot?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />
    </>
  );
};

export default Chatbots;
